'use client';

import { c as _c } from "react/compiler-runtime";
import classnames from 'classnames';
import { useLocale } from 'next-intl';
import { ResourceTypeIcon } from '@module/mdrt-org/shared/components/resource-type-icon/resource-type-icon';
import { getExternalUrlOrRelativePath } from '@module/mdrt-org/shared/utils/helpers/get-external-url-or-relative-path';
import { isShortcutExternal } from '@module/mdrt-org/shared/utils/helpers/is-shortcut-external';
import { ChipTheme } from '@shared/ui-components/chip/chipTheme';
import { RenderHTML } from '@shared/ui-components/render-html/render-html';
import { GTM_HERO_CAROUSEL_HEADER_VALUE } from '@shared/utils/constants/gtm';
import { useFormatTranslationString } from '@shared/utils/helpers/format-translation-string';
import { getTextFromHtmlString } from '@shared/utils/helpers/get-text-from-html-string';
import { mapIntlLocaleToOptimizelyLocale } from '@shared/utils/helpers/map-intl-locale-to-optimizely-locale';
import { BookmarkButtonClient } from '../../../../../../components/bookmark-button/bookmark-button-client';
import { type BookmarkButtonCmsContent } from '../../../../../../components/bookmark-button/get-bookmark-button-cms-content';
import { Chip } from '../../../../../../components/chip/chip';
import { ImageWrapper } from '../../../../../../components/image-wrapper/image-wrapper';
import { type ShareDropdownCmsContent } from '../../../../../../components/share-dropdown/get-share-dropdown-cms-content';
import { ShareDropdownClient } from '../../../../../../components/share-dropdown/share-dropdown-client';
import { DEFAULT_LOCALE } from '../../../../../../i18n/locales';
import { type LearnContentCarouselCardFragment } from '../../../../../../utils/data/graphql/_generated/gql-generated';
import { convertSecondsToMinutes } from '../../../../../../utils/helpers/convert-seconds-to-minutes';
import { getCardLinkDescription } from '../../../../../../utils/helpers/get-card-link-description';
import { getContentFallbackImageSource } from '../../../../../../utils/helpers/get-content-fallback-image-source';
import { getFeaturedPeopleOrAuthors } from '../../../../../../utils/helpers/get-featured-people-or-authors';
import styles from './learn-hero-carousel-card.module.scss';
export type LearnHeroCarouselCardProps = {
  bookmarkButtonCmsContent: BookmarkButtonCmsContent;
  card: LearnContentCarouselCardFragment;
  isBookmarked: boolean;
  minutesLabel: string;
  shareDropdownCmsContent: ShareDropdownCmsContent;
  viewActionLabel: string;
};
export const LearnHeroCarouselCard = t0 => {
  const $ = _c(68);
  const {
    bookmarkButtonCmsContent,
    card,
    isBookmarked,
    minutesLabel,
    shareDropdownCmsContent,
    viewActionLabel
  } = t0;
  const formatTranslationString = useFormatTranslationString();
  const url = card.ContentLink?.Url;
  const isExternal = isShortcutExternal(card.Shortcut, url);
  const t1 = card?.ContentTypeSelection;
  let t2;
  if ($[0] !== t1) {
    t2 = getContentFallbackImageSource(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const fallbackImageSource = t2;
  const imageAlt = card?.ThumbnailImage?.AltText;
  const people = getFeaturedPeopleOrAuthors(card);
  const firstAuthor = people[0];
  const otherAuthorsCount = firstAuthor && people.length > 1 ? people.length - 1 : undefined;
  const description = getCardLinkDescription(card?.SubheadingText, card?.Subheading);
  const locale = useLocale();
  let t3;
  if ($[2] !== locale) {
    t3 = mapIntlLocaleToOptimizelyLocale(locale).toLowerCase();
    $[2] = locale;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const localeUrlSegment = t3;
  let t4;
  if ($[4] !== card.RelativePath || $[5] !== locale || $[6] !== localeUrlSegment) {
    t4 = locale === DEFAULT_LOCALE ? card.RelativePath : card.RelativePath?.replace(/^\/\w+\//u, `/${localeUrlSegment}/`);
    $[4] = card.RelativePath;
    $[5] = locale;
    $[6] = localeUrlSegment;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const formattedRelativePath = t4;
  const formattedUrl = getExternalUrlOrRelativePath({
    isExternal,
    relativePath: `${process.env.NEXT_PUBLIC_BASE_URL || window.location.origin}${formattedRelativePath}`,
    url
  });
  let t5;
  if ($[8] !== card.ReadTimeInSeconds || $[9] !== formatTranslationString || $[10] !== minutesLabel) {
    t5 = card.ReadTimeInSeconds && card.ReadTimeInSeconds > 0 ? formatTranslationString(minutesLabel, {
      readTime: convertSecondsToMinutes(card.ReadTimeInSeconds)
    }) : null;
    $[8] = card.ReadTimeInSeconds;
    $[9] = formatTranslationString;
    $[10] = minutesLabel;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const formattedReadTimeLabel = t5;
  const t6 = styles["learn-hero-carousel-card"];
  const t7 = 0;
  const t8 = styles["learn-hero-carousel-card__content"];
  const t9 = people.join("; ");
  let t10;
  if ($[12] !== firstAuthor) {
    t10 = <span className={styles["learn-hero-carousel-card__first-author"]}>{firstAuthor}</span>;
    $[12] = firstAuthor;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== otherAuthorsCount) {
    t11 = otherAuthorsCount && <span className={styles["learn-hero-carousel-card__author-count"]}>+{otherAuthorsCount}</span>;
    $[14] = otherAuthorsCount;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t11 || $[18] !== t9) {
    t12 = <p className={styles["learn-hero-carousel-card__authors"]} title={t9}>{t10}{t11}</p>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t9;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== firstAuthor || $[21] !== formattedReadTimeLabel) {
    t13 = formattedReadTimeLabel && <span className={classnames(styles["learn-hero-carousel-card__read-time"], {
      [styles["learn-hero-carousel-card__read-time--with-separator"]]: firstAuthor
    })}>{formattedReadTimeLabel}</span>;
    $[20] = firstAuthor;
    $[21] = formattedReadTimeLabel;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t12 || $[24] !== t13) {
    t14 = <div className={styles["learn-hero-carousel-card__info"]}>{t12}{t13}</div>;
    $[23] = t12;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== card.Heading) {
    t15 = card?.Heading && <h1 className={styles["learn-hero-carousel-card__title"]} title={card.Heading}>{card.Heading}</h1>;
    $[26] = card.Heading;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const T0 = RenderHTML;
  const t16 = styles["learn-hero-carousel-card__description"];
  const t17 = getTextFromHtmlString(description);
  let t18;
  if ($[28] !== T0 || $[29] !== description || $[30] !== t16 || $[31] !== t17) {
    t18 = <T0 className={t16} html={description} title={t17} />;
    $[28] = T0;
    $[29] = description;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const t19 = card?.ContentTypeSelection;
  let t20;
  if ($[33] !== isExternal || $[34] !== t19) {
    t20 = <ResourceTypeIcon contentType={t19} isExternal={isExternal} />;
    $[33] = isExternal;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  const t21 = isExternal ? "_blank" : undefined;
  const t22 = formattedUrl || "#";
  let t23;
  if ($[36] !== t20 || $[37] !== t21 || $[38] !== t22 || $[39] !== viewActionLabel) {
    t23 = <Chip data-ga-carousel-header={GTM_HERO_CAROUSEL_HEADER_VALUE} icon={t20} label={viewActionLabel} target={t21} theme={ChipTheme.Emphasis} to={t22} />;
    $[36] = t20;
    $[37] = t21;
    $[38] = t22;
    $[39] = viewActionLabel;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  const t24 = card.ContentLink?.GuidValue;
  let t25;
  if ($[41] !== bookmarkButtonCmsContent || $[42] !== card.ContentTypeSelection || $[43] !== isBookmarked || $[44] !== t24) {
    t25 = <BookmarkButtonClient cmsContent={bookmarkButtonCmsContent} contentType={card.ContentTypeSelection} initialIsBookmarked={isBookmarked} learningMaterialId={t24} />;
    $[41] = bookmarkButtonCmsContent;
    $[42] = card.ContentTypeSelection;
    $[43] = isBookmarked;
    $[44] = t24;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== card.Heading || $[47] !== formattedUrl || $[48] !== shareDropdownCmsContent) {
    t26 = <ShareDropdownClient cmsContent={shareDropdownCmsContent} menuPlacement="bottom left" title={card.Heading} url={formattedUrl} />;
    $[46] = card.Heading;
    $[47] = formattedUrl;
    $[48] = shareDropdownCmsContent;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t23 || $[51] !== t25 || $[52] !== t26) {
    t27 = <div className={styles["learn-hero-carousel-card__actions"]}>{t23}{t25}{t26}</div>;
    $[50] = t23;
    $[51] = t25;
    $[52] = t26;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] !== t14 || $[55] !== t15 || $[56] !== t18 || $[57] !== t27 || $[58] !== t8) {
    t28 = <div className={t8}>{t14}{t15}{t18}{t27}</div>;
    $[54] = t14;
    $[55] = t15;
    $[56] = t18;
    $[57] = t27;
    $[58] = t8;
    $[59] = t28;
  } else {
    t28 = $[59];
  }
  const t29 = imageAlt ?? card?.Heading ?? "";
  const t30 = card?.ThumbnailImage?.ImageReference?.Url ?? "";
  let t31;
  if ($[60] !== fallbackImageSource || $[61] !== t29 || $[62] !== t30) {
    t31 = <ImageWrapper alt={t29} className={styles["learn-hero-carousel-card__image"]} fallbackSrc={fallbackImageSource} fetchPriority="high" height={800} loading="eager" preload={true} sizes="(min-width: 940px) 33vw, (min-width: 480px) 52vw, 100vw" src={t30} width={800} />;
    $[60] = fallbackImageSource;
    $[61] = t29;
    $[62] = t30;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== t28 || $[65] !== t31 || $[66] !== t6) {
    t32 = <div className={t6} tabIndex={t7}>{t28}{t31}</div>;
    $[64] = t28;
    $[65] = t31;
    $[66] = t6;
    $[67] = t32;
  } else {
    t32 = $[67];
  }
  return t32;
};